import * as MP4Box from 'mp4box';

// Sliding window: decoded frames kept ahead of playhead (~4s at 30fps).
const WINDOW_AHEAD = 120;
// Frames kept behind playhead to handle time jitter (~0.5s at 30fps).
const WINDOW_BEHIND = 15;

// Pre-buffer frames before playback starts.
const PREFILL_FRAMES = WINDOW_AHEAD;
// Max time for pre-buffering to avoid hanging on slow devices.
const PREFILL_DEADLINE_MS = 6000;

// Decoder input queue depth limit.
const DECODE_QUEUE_LIMIT = 24;
// Max concurrent createImageBitmap operations.
const MAX_INFLIGHT_CONVERSIONS = 24;

// Starvation detection threshold (device can't decode in real-time).
const STARVATION_THRESHOLD_MS = 4000;

const MAX_HEIGHT = 480;

export default class WebCodecsBuffer {
    constructor(videoUrl, onReady, onError, onStarved) {
        this.videoUrl = videoUrl;
        this.onReady = onReady;
        this.onError = onError;
        this.onStarved = onStarved;

        this.duration = 0;
        this.fps = 30;
        this.frameCount = 0;

        this.chunks = [];               
        this.keyframeIndices = [];      
        this.timestampToIndex = new Map(); 
        this.presentationOrder = [];

        this.window = new Map();
        this._pending = new Set();

        this.decoder = null;
        this.decoderConfig = null;
        this.mp4boxfile = null;

        this._decodeCursor = 0;
        this._runStart = 0;
        this._currentDecodeTarget = 0;
        this._lastReturnedIndex = null;
        this._lastReturnedBitmap = null;
        this._lastPresentation = null;
        this._endFlushed = false;
        this._destroyed = false;

        this._starvedMs = 0;
        this._lastHealthCheck = 0;
        this._starvationReported = false;

        this.init();
    }

    async init() {
        const mp4boxfile = MP4Box.createFile();
        this.mp4boxfile = mp4boxfile;
        let isConfigured = false;

        const decoder = new VideoDecoder({
            output: (frame) => {
                const p = this._processFrame(frame).finally(() => this._pending.delete(p));
                this._pending.add(p);
            },
            error: (e) => {
                if (this.onError) this.onError(e);
            },
        });
        this.decoder = decoder;

        const parsed = new Promise((resolve, reject) => {
            mp4boxfile.onReady = (info) => {
                const videoTrack = info.videoTracks[0];
                if (!videoTrack) {
                    return reject(new Error('No video track found in file.'));
                }

                this.duration = videoTrack.duration / videoTrack.timescale;
                this._expectedSamples = videoTrack.nb_samples;

                let description = undefined;
                const trak = mp4boxfile.getTrackById(videoTrack.id);
                if (trak) {
                    for (const entry of trak.mdia.minf.stbl.stsd.entries) {
                        const box = entry.avcC || entry.hvcC || entry.vpcC || entry.av1C;
                        if (box) {
                            const stream = new MP4Box.DataStream(undefined, 0, MP4Box.DataStream.BIG_ENDIAN);
                            box.write(stream);
                            description = new Uint8Array(stream.buffer, 8);
                            break;
                        }
                    }
                }

                try {
                    this.decoderConfig = {
                        codec: videoTrack.codec,
                        codedWidth: videoTrack.video.width,
                        codedHeight: videoTrack.video.height,
                        description,
                        optimizeForLatency: true,
                        hardwareAcceleration: 'prefer-hardware',
                    };
                    decoder.configure(this.decoderConfig);
                    isConfigured = true;
                } catch (e) {
                    return reject(new Error(`Decoder configuration failed: ${e.message}`));
                }

                mp4boxfile.setExtractionOptions(videoTrack.id, null, { nbSamples: videoTrack.nb_samples });
                mp4boxfile.start();
            };

            mp4boxfile.onSamples = (id, user, samples) => {
                for (const sample of samples) {
                    const idx = this.chunks.length;
                    const ts = sample.cts * 1000;
                    this.chunks.push({
                        type: sample.is_sync ? 'key' : 'delta',
                        timestamp: ts,
                        duration: sample.duration * 1000,
                        data: sample.data,
                    });
                    this.timestampToIndex.set(ts, idx);
                    if (sample.is_sync) this.keyframeIndices.push(idx);
                }
                if (this.chunks.length >= this._expectedSamples) {
                    resolve();
                }
            };

            mp4boxfile.onError = (e) => reject(new Error(`mp4box Error: ${e}`));
        });

        try {
            const response = await fetch(this.videoUrl);
            if (!response.ok) {
                throw new Error(`HTTP Fetch failed with status ${response.status}`);
            }

            const arrayBuffer = await response.arrayBuffer();
            arrayBuffer.fileStart = 0;
            mp4boxfile.appendBuffer(arrayBuffer);
            mp4boxfile.flush();

            if (!isConfigured) {
                throw new Error('mp4box could not parse the file. Is it a valid .mp4 video? (Note: .webm or .gif will NOT work with mp4box)');
            }

            await parsed;

            if (this._destroyed) return;

            if (this.keyframeIndices.length === 0) this.keyframeIndices.push(0);
            this.frameCount = this.chunks.length;
            this.fps = this.duration > 0 ? this.frameCount / this.duration : 30;

            this.presentationOrder = Array.from({ length: this.frameCount }, (_, i) => i)
                .sort((a, b) => this.chunks[a].timestamp - this.chunks[b].timestamp);

            const firstKeyframe = this.keyframeIndices[0] || 0;
            this._runStart = firstKeyframe;
            this._decodeCursor = firstKeyframe;
            this._currentDecodeTarget = firstKeyframe;
            this._lastReturnedIndex = null;
            this._endFlushed = false;

            const prefillTarget = Math.min(PREFILL_FRAMES, this.frameCount);
            const prefillDeadline = performance.now() + PREFILL_DEADLINE_MS;

            while (
                this.window.size < prefillTarget &&
                performance.now() < prefillDeadline &&
                !this._destroyed &&
                this.decoder.state === 'configured'
            ) {
                this._pump(firstKeyframe);
                await new Promise((resolve) => setTimeout(resolve, 8));
            }

            if (this._destroyed) return;
            if (this.window.size === 0) {
                throw new Error('Decode produced no frames during warmup.');
            }

            if (this.onReady) this.onReady();
        } catch (error) {
            if (this.decoder && this.decoder.state !== 'closed') {
                this.decoder.close();
            }
            if (this.onError) this.onError(error);
        }
    }

    async _processFrame(frame) {
        let bitmap = null;
        try {
            const ts = frame.timestamp;
            const idx = this.timestampToIndex.get(ts);
            if (idx === undefined || !this._inKeepRange(idx)) {
                frame.close();
                return;
            }

            if (frame.displayHeight > MAX_HEIGHT) {
                const scale = MAX_HEIGHT / frame.displayHeight;
                const newWidth = Math.round(frame.displayWidth * scale);
                bitmap = await createImageBitmap(frame, {
                    resizeWidth: newWidth,
                    resizeHeight: MAX_HEIGHT,
                    resizeQuality: 'low',
                });
            } else {
                bitmap = await createImageBitmap(frame);
            }
            frame.close();

            if (this._destroyed || !this._inKeepRange(idx)) {
                bitmap.close();
                return;
            }

            const existing = this.window.get(idx);
            if (existing) existing.bitmap.close();
            this.window.set(idx, { bitmap, timestamp: ts / 1000000 });
        } catch (e) {
            try { frame.close(); } catch (_) { /* already closed */ }
            if (bitmap) { try { bitmap.close(); } catch (_) { /* noop */ } }
        }
    }

    _keepBounds() {
        const target = this._currentDecodeTarget;
        const pin = this._lastReturnedIndex == null ? target : this._lastReturnedIndex;
        return {
            low: Math.min(target - WINDOW_BEHIND, pin),
            high: target + WINDOW_AHEAD,
        };
    }

    _inKeepRange(idx) {
        const { low, high } = this._keepBounds();
        return idx >= low && idx <= high;
    }

    _evict() {
        const { low, high } = this._keepBounds();
        for (const [idx, entry] of this.window) {
            if (idx < low || idx > high) {
                entry.bitmap.close();
                this.window.delete(idx);
            }
        }
    }

    _pump(decodeTarget, forceSeek = false) {
        const dec = this.decoder;
        if (!dec || dec.state !== 'configured') return;

        if (forceSeek || decodeTarget < this._runStart) {
            this._seekToKeyframe(decodeTarget);
        }

        while (
            this._decodeCursor < this.chunks.length &&
            this._decodeCursor <= decodeTarget + WINDOW_AHEAD &&
            dec.decodeQueueSize < DECODE_QUEUE_LIMIT &&
            this._pending.size < MAX_INFLIGHT_CONVERSIONS
        ) {
            const c = this.chunks[this._decodeCursor];
            dec.decode(new EncodedVideoChunk({
                type: c.type,
                timestamp: c.timestamp,
                duration: c.duration,
                data: c.data,
            }));
            this._decodeCursor++;
        }

        if (this._decodeCursor >= this.chunks.length && !this._endFlushed && dec.decodeQueueSize === 0) {
            this._endFlushed = true;
            dec.flush().catch(() => { /* expected to be aborted on loop reset */ });
        }
    }

    _seekToKeyframe(decodeTarget) {
        const dec = this.decoder;

        let kf = this.keyframeIndices[0] || 0;
        for (let i = 0; i < this.keyframeIndices.length; i++) {
            if (this.keyframeIndices[i] <= decodeTarget) kf = this.keyframeIndices[i];
            else break;
        }

        try {
            if (dec.state !== 'closed') {
                dec.reset();  
                dec.configure(this.decoderConfig);
            }
        } catch (_) {
            /* ignore reconfigure errors to prevent player freeze on seek */
        }

        this._runStart = kf;
        this._decodeCursor = kf;
        this._endFlushed = false;
        this._lastReturnedIndex = null;
    }

    _pickPresentation(p) {
        for (let d = 0; d <= WINDOW_BEHIND; d++) {
            const pp = p - d;
            if (pp < 0) break;
            const di = this.presentationOrder[pp];
            if (this.window.has(di)) return di;
        }
        for (let d = 1; d <= WINDOW_AHEAD; d++) {
            const pp = p + d;
            if (pp >= this.frameCount) break;
            const di = this.presentationOrder[pp];
            if (this.window.has(di)) return di;
        }
        return -1;
    }

    getFrameAtTime(timeSeconds) {
        if (this.frameCount === 0 || this.duration <= 0) return this._lastReturnedBitmap;

        const safeTime = ((timeSeconds % this.duration) + this.duration) % this.duration;
        const p = Math.floor(safeTime * this.fps) % this.frameCount; 
        const decodeTarget = this.presentationOrder[p];

        const wrapped = this._lastPresentation != null &&
            p < this._lastPresentation - this.frameCount / 2;
        this._lastPresentation = p;

        this._currentDecodeTarget = decodeTarget;
        this._pump(decodeTarget, wrapped);
        this._evict();
        this._updateHealth(this.window.has(decodeTarget));

        const di = this._pickPresentation(p);
        if (di >= 0) {
            this._lastReturnedIndex = di;
            this._lastReturnedBitmap = this.window.get(di).bitmap;
            return this._lastReturnedBitmap;
        }
        return this._lastReturnedBitmap;
    }

    _updateHealth(healthy) {
        if (this._starvationReported || !this.onStarved) return;

        const now = performance.now();
        const dt = this._lastHealthCheck ? Math.min(now - this._lastHealthCheck, 100) : 0;
        this._lastHealthCheck = now;

        if (healthy) {
            this._starvedMs = Math.max(0, this._starvedMs - dt);
            return;
        }

        this._starvedMs += dt;
        if (this._starvedMs >= STARVATION_THRESHOLD_MS) {
            this._starvationReported = true;
            const cb = this.onStarved;
            setTimeout(() => { if (cb) cb(); }, 0);
        }
    }

    getFirstFrame() {
        if (this.frameCount === 0) return null;
        const di = this.presentationOrder[0];
        if (this.window.has(di)) return this.window.get(di).bitmap;
        for (const entry of this.window.values()) return entry.bitmap;
        return null;
    }

    destroy() {
        this._destroyed = true;

        try {
            if (this.decoder && this.decoder.state !== 'closed') this.decoder.close();
        } catch (_) { /* noop */ }

        for (const entry of this.window.values()) {
            try { entry.bitmap.close(); } catch (_) { /* noop */ }
        }
        this.window.clear();

        this._lastReturnedBitmap = null;
        this.frameCount = 0;
        this.chunks = [];
        this.keyframeIndices = [];
        this.presentationOrder = [];
        this.timestampToIndex.clear();
        this.mp4boxfile = null;
    }
}
